import React, { useMemo, useEffect, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import {
  BrainCircuit, TrendingUp, DollarSign,
  AlertTriangle, CheckCircle2, ArrowRight, Sparkles
} from 'lucide-react';
import { ProblemSolutionCard } from './ProblemSolutionCard.jsx';
import { formatCurrency, formatIndustryLabel } from '../../utils/reportHelpers';

// Animated number counter
const AnimatedCounter = ({ value, prefix = '', suffix = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    const duration = 1200;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      ref.current.textContent = prefix + formatCurrency(Math.round(value * eased)) + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value, prefix, suffix]);
  return <span ref={ref}>{prefix}{formatCurrency(value || 0)}{suffix}</span>;
};

const _tFallback = (key) => key.split('.').pop();
export const DiagnosisTab = ({ fullReport, industry, subVertical, t = _tFallback, onGoToTool = null, progress = {}, businessSize = '' }) => {
  // Round-14 (Sarah R13 verdict): hard-clamp to actual RESOLVED-chip
  // count, not selected-chip count. Backend's R11 chip-only mode sets
  // matched_problems = chip_matched (resolved IDs), so its length IS
  // the resolved-chip count. Trust the backend; don't pad up to
  // selected-chip count. R12's slice(0, chipsSelected.length) was a
  // defensive cap but it allowed up to the labels-count, which left
  // a gap if a code path padded matched_problems between backend and
  // render. Now we trust matched_problems.length directly: if the
  // backend resolved 2 chips, we show 2 cards, period.
  const problems = fullReport?.ownerProblems?.matched_problems ?? [];
  const recordsAnalyzed = fullReport?.dataDiagnostics?.recordsAnalyzed || '348+ tools';

  // Round-11: slug → display name map. Diagnosis tab was rendering raw
  // tool IDs ("shopify_ai addresses this in Phase 1", "Start fixing
  // this → shopify_ai") — Sarah called this out as a buyer-trust
  // killer. Map is built once from the recommendations array; falls
  // back to the raw token if no match (defensive for legacy reports).
  const toolNameMap = useMemo(() => {
    const m = {};
    for (const tool of (fullReport?.aiToolsRecommendations || [])) {
      const id = tool?.id || tool?.tool_name || tool?.name;
      const name = tool?.tool_name || tool?.name || tool?.id;
      if (id) m[id] = name || id;
    }
    return m;
  }, [fullReport]);
  const displayName = (slugOrId) =>
    toolNameMap[slugOrId] || slugOrId || '';

  const stats = useMemo(() => {
    const highPriority = problems.filter(p => p.severity === 'HIGH').length;
    // Naive sum of monthly losses — kept for the per-problem cards but
    // intentionally NOT rendered as the headline number any more.
    const losses = problems
      .map(p => p.median_loss_per_month || 0)
      .filter(v => v > 0)
      .sort((a, b) => b - a);
    const totalMonthlyLossRaw = losses.reduce((s, v) => s + v, 0);

    // Overlap-dampened high-end: highest single problem at full weight, the
    // rest at 50%. Reasoning: fixing no-shows AND fixing case acceptance
    // overlap on the same patient slots — they don't independently each
    // recover their full claimed amount. The reviewer flag was that for a
    // 1-5 employee dental practice, the unadjusted sum was claiming losses
    // of >70% of revenue, which buyers immediately reject as inflated.
    const dampenedHigh = losses.length === 0
      ? 0
      : losses[0] + losses.slice(1).reduce((s, v) => s + v * 0.5, 0);

    // Honest floor: just the highest single problem.
    const dampenedLow = losses[0] || 0;

    // Revenue ceiling: cap the high end at 50% of monthly revenue when
    // we know it. SMBs don't lose 70%+ of revenue to fixable workflow gaps.
    const monthlyRevenueAssumed =
      fullReport?.roiProjections?.assumptions?.monthlyRevenueAssumed
      || 0;
    const cappedHigh = monthlyRevenueAssumed > 0
      ? Math.min(dampenedHigh, monthlyRevenueAssumed * 0.5)
      : dampenedHigh;
    const cappedLow = monthlyRevenueAssumed > 0
      ? Math.min(dampenedLow, monthlyRevenueAssumed * 0.25)
      : dampenedLow;

    // Prefer backend-shipped range when present (single source of truth).
    const backendLow = (fullReport?.roiProjections?.cost_of_inaction_12m_low || 0) / 12;
    const backendHigh = (fullReport?.roiProjections?.cost_of_inaction_12m_high || 0) / 12;
    const monthlyLossLow = Math.round(backendLow > 0 ? backendLow : cappedLow);
    const monthlyLossHigh = Math.round(backendHigh > 0 ? backendHigh : cappedHigh);

    const solvable = problems.filter(p => p.recommended_tools?.length > 0).length;
    const alreadyFixed = problems.filter(p =>
      p.recommended_tools?.some(toolId => progress?.tools?.[toolId] === 'done')
    ).length;
    return {
      highPriority,
      // Legacy field preserved for any consumers that still read it.
      totalMonthlyLoss: monthlyLossHigh,
      monthlyLossLow,
      monthlyLossHigh,
      totalMonthlyLossRaw,
      revenueCapApplied: monthlyRevenueAssumed > 0 && dampenedHigh > monthlyRevenueAssumed * 0.5,
      solvable,
      alreadyFixed,
    };
  }, [problems, progress, fullReport]);

  const sorted = useMemo(() =>
    [...problems].sort((a, b) => {
      const order = { HIGH: 0, MEDIUM: 1, LOW: 2 };
      return (order[a.severity] ?? 2) - (order[b.severity] ?? 2);
    }),
    [problems]
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BrainCircuit className="w-8 h-8 text-[#3b82f6] flex-shrink-0" />
        <div>
          <h2 className="text-2xl font-bold text-white">{t('report.diagnosis.title')}</h2>
          <p className="text-slate-400 text-sm">
            {(() => {
              // Only headline a sample size if it's statistically meaningful.
              // "13 similar businesses" looks weak — hide the number below 30.
              const n = typeof recordsAnalyzed === 'number'
                ? recordsAnalyzed
                : parseInt(String(recordsAnalyzed || '').replace(/[^\d]/g, ''), 10);
              const _industryLabel = industry ? formatIndustryLabel(industry, 'SMB', subVertical) : 'SMB';
              if (!Number.isFinite(n) || n < 30) {
                return <>Matched against 2026 {_industryLabel} industry benchmarks and problem-pattern data.</>;
              }
              return (
                <>
                  Based on analysis of{' '}
                  <span className="text-[#3b82f6] font-semibold">{recordsAnalyzed}</span>{' '}
                  similar businesses in the {_industryLabel} space.
                </>
              );
            })()}
          </p>
        </div>
      </div>

      {/* Cost of inaction banner — rendered as a credible RANGE, not a
          naive sum. The earlier "Combined across all N problems" copy
          summed every matched problem's median_loss_per_month and
          produced figures that exceeded 50% of a small SMB's revenue,
          which buyers pattern-match as inflated and stop trusting the
          rest of the report. */}
      {stats.monthlyLossHigh > 0 && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-6 h-6 text-red-400" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-0.5">{t('report.diagnosis.costOfInaction')}</p>
            <p className="text-3xl font-bold text-white">
              {stats.monthlyLossLow > 0 && stats.monthlyLossLow < stats.monthlyLossHigh ? (
                <>
                  <AnimatedCounter value={stats.monthlyLossLow} />
                  <span className="text-slate-500 text-2xl mx-1">–</span>
                  <AnimatedCounter value={stats.monthlyLossHigh} />
                </>
              ) : (
                <AnimatedCounter value={stats.monthlyLossHigh} />
              )}
              <span className="text-slate-400 text-lg font-normal">{t('report.diagnosis.perMonth')}</span>
            </p>
            <p className="text-sm text-slate-400 mt-1">
              At risk across <span className="text-white font-semibold">{problems.length}</span> overlapping issues —
              fixing one usually recovers part of the others, so we cite a range, not a sum.
              {stats.revenueCapApplied && (
                <span className="text-slate-500"> Capped at half your monthly revenue for plausibility.</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/25 rounded-xl px-4 py-3 flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 text-[#3b82f6] flex-shrink-0" />
            <div>
              <p className="text-[#3b82f6] font-bold text-lg">{stats.solvable}/{problems.length}</p>
              <p className="text-xs text-slate-400">{t('report.diagnosis.solvedLabel')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Priority action banner */}
      {sorted[0] && (
        <div className="rounded-xl border border-blue-500/25 bg-blue-500/5 px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-[#3b82f6] flex-shrink-0" />
            <p className="text-sm text-slate-300">
              <span className="text-white font-semibold">Top priority: </span>
              {sorted[0].problem} —{sorted[0].recommended_tools?.[0]
                ? ` ${displayName(sorted[0].recommended_tools[0])} addresses this in Phase 1`
                : ' solution included in your blueprint'}
            </p>
          </div>
          {onGoToTool && sorted[0].recommended_tools?.[0] && (
            <button
              onClick={() => onGoToTool(sorted[0].recommended_tools[0])}
              className="text-xs text-[#3b82f6] border border-blue-500/30 rounded-full px-3 py-1.5 whitespace-nowrap hover:border-blue-400/50 transition-colors flex-shrink-0"
            >
              View fix →
            </button>
          )}
        </div>
      )}

      {/* Summary scorecard */}
      {problems.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Priority Issues */}
          <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">{t('report.diagnosis.priorityIssues')}</p>
                <p className="text-xl font-bold text-white">
                  {stats.highPriority} high, {problems.length - stats.highPriority} other
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{problems.length} total identified</p>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Leakage */}
          {stats.totalMonthlyLoss > 0 ? (
            <Card className="bg-slate-900/70 backdrop-blur-xl border border-amber-500/25">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Est. Monthly Leakage</p>
                  <p className="text-xl font-bold text-amber-400">
                    {formatCurrency(stats.totalMonthlyLoss)}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">recoverable with this blueprint</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-900/70 backdrop-blur-xl border border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Est. Monthly Leakage</p>
                  <p className="text-xl font-bold text-white">{t('report.diagnosis.calculating')}</p>
                  <p className="text-xs text-slate-500 mt-0.5">based on your problem profile</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Solvable */}
          <Card className="bg-slate-900/70 backdrop-blur-xl border border-blue-500/25">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-[#3b82f6]" />
              </div>
              <div>
                <p className="text-sm text-slate-400">{t('report.diagnosis.fixableProblems')}</p>
                <p className="text-xl font-bold text-white">
                  {stats.solvable} of {problems.length}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">have tool solutions ready</p>
                {stats.alreadyFixed > 0 && (
                  <p className="text-xs text-[#3b82f6] mt-0.5">
                    {stats.alreadyFixed} already being addressed
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Problem cards */}
      <div className="space-y-6">
        {sorted.length > 0 ? (
          sorted.map((problem, i) => (
            <div key={problem.id ?? i}>
              <ProblemSolutionCard
                problem={problem}
                index={i}
                industry={industry}
                businessSize={businessSize}
              />
              {/* Per-problem insights row was here. ProblemSolutionCard already
                  renders an identical Signs / Root cause / $-impact / prevalence
                  footer inside the card itself — duplicating it here meant the
                  buyer saw the same data twice for every problem, which signals
                  "we're padding for value" instead of "we said it once and well." */}
              {problem.recommended_tools?.length > 0 && onGoToTool && (
                <button
                  onClick={() => onGoToTool(problem.recommended_tools[0])}
                  className="mt-2 text-xs text-[#3b82f6] hover:text-blue-300 border border-blue-500/25 hover:border-blue-400/50 rounded-full px-3 py-1.5 transition-colors inline-flex items-center gap-1.5"
                >
                  <ArrowRight className="w-3 h-3" />
                  Start fixing this → {displayName(problem.recommended_tools[0])}
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-16 text-slate-400 bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl">
            <BrainCircuit className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>{t('report.diagnosis.processing')}</p>
          </div>
        )}
      </div>

      {/* Trust footer — hide sample-size claim when N<30 (same threshold as the header) */}
      {problems.length > 0 && (() => {
        const n = typeof recordsAnalyzed === 'number'
          ? recordsAnalyzed
          : parseInt(String(recordsAnalyzed || '').replace(/[^\d]/g, ''), 10);
        const showN = Number.isFinite(n) && n >= 30;
        return (
          <div className="text-center text-xs text-slate-600 border-t border-white/8 pt-4">
            {showN
              ? <>Problem prevalence and impact data derived from {recordsAnalyzed} anonymized SMB profiles. Models updated quarterly.</>
              : <>Problem prevalence and impact data derived from 2026 {industry ? formatIndustryLabel(industry, 'SMB', subVertical) : 'SMB'} industry benchmarks. Models updated quarterly.</>}
          </div>
        );
      })()}
    </div>
  );
};